import type React from "react";

const SITE_URL = "https://cyclecollective.ca";
const DEFAULT_IMAGE = `${SITE_URL}/images/hero-bg.jpg`;

interface SeoProps {
  /** Page title. " | Cycle Collective Saskatoon" is appended automatically. */
  title: string;
  /** Meta description for search results and social cards. */
  description: string;
  /** Route path, e.g. "/schedule". Used for canonical + og:url. */
  path: string;
  /** Absolute or root-relative image for social cards. Defaults to the hero image. */
  image?: string;
}

/**
 * Renders per-route SEO tags. React 19 hoists <title>/<meta>/<link>
 * rendered here into <head>, so this can live anywhere in a page.
 */
const Seo: React.FC<SeoProps> = ({ title, description, path, image }) => {
  const fullTitle = `${title} | Cycle Collective Saskatoon`;
  const url = `${SITE_URL}${path}`;
  const img = image
    ? image.startsWith("http")
      ? image
      : `${SITE_URL}${image}`
    : DEFAULT_IMAGE;

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Cycle Collective Saskatoon" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content="en_CA" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={img} />
    </>
  );
};

export default Seo;
